import { z } from 'zod';

/**
 * THE TUTORIAL CATALOGUE — what a shop owner is shown when they do not know how to do a thing.
 *
 * This product is sold to people who are not software users. They run a tailoring shop, they
 * were handed a login, and when they get stuck at nine in the evening there is nobody in the
 * building who can help. Until now the app had NOTHING to say to them: no guide, no walkthrough,
 * no way to ask. That is not a missing nicety, it is the reason a shop stops opening the app.
 *
 * Three decisions shape this file.
 *
 * **The catalogue is code, not content in a database.** Every tutorial is about a screen that
 * exists in this repository, and a walkthrough that drifts from the screen it describes is
 * worse than no walkthrough at all — it teaches somebody a sequence of taps that no longer
 * works and then they stop trusting the whole section. Keeping it here means a screen renamed
 * in a pull request is a tutorial renamed in the same pull request.
 *
 * **Every tutorial names a route it teaches**, so the UI can offer "open that screen" instead
 * of asking somebody to find it again from a menu. The commonest failure of in-app help is
 * that reading it and doing it happen in two different places.
 *
 * **A video is optional and absent by default.** Recording ten screencasts is a content
 * project, not a code one, and shipping empty video players in the meantime would make the
 * section look broken. `videoUrl` is `null` here, the UI renders no player when it is, and a
 * deployment that has recorded them fills the field in.
 */

/* -------------------------------------------------------------------------------------------
 * Grouping
 * ---------------------------------------------------------------------------------------- */

/**
 * The sections a tutorial is filed under.
 *
 * Named after what somebody is TRYING TO DO rather than after our modules. "Getting paid" is a
 * thing a shop owner wants; "invoices" is a thing we built. Somebody searching help does not
 * know our nouns yet — that is exactly why they are here.
 */
export const TUTORIAL_TOPICS = [
  'getting_started',
  'taking_orders',
  'the_workshop',
  'getting_paid',
  'the_people',
  'the_shop',
] as const;
export type TutorialTopic = (typeof TUTORIAL_TOPICS)[number];
export const tutorialTopicSchema = z.enum(TUTORIAL_TOPICS);

export const TUTORIAL_TOPIC_LABELS: Readonly<Record<TutorialTopic, string>> = Object.freeze({
  getting_started: 'Getting started',
  taking_orders: 'Taking orders',
  the_workshop: 'The workshop',
  getting_paid: 'Getting paid',
  the_people: 'Your people',
  the_shop: 'Your shop',
});

/* -------------------------------------------------------------------------------------------
 * One tutorial
 * ---------------------------------------------------------------------------------------- */

export interface TutorialDefinition {
  readonly id: string;
  readonly topic: TutorialTopic;
  /** Default English; the UI resolves `support.tutorial.<id>.title` through i18next. */
  readonly defaultTitle: string;
  /** One sentence saying what somebody will be able to do afterwards. */
  readonly defaultSummary: string;
  /**
   * The steps, in order. Written as instructions to a person at a counter, not as a
   * description of a feature — "tap Orders, then Add garment" rather than "the order form
   * supports multiple garments".
   */
  readonly defaultSteps: readonly string[];
  /** The screen this teaches, so the reader can go straight there. */
  readonly route: string;
  /** A recorded walkthrough, when one exists. See the note at the top of this file. */
  readonly videoUrl: string | null;
  /**
   * True for the handful somebody genuinely needs in their first week. What the support
   * screen puts at the top before anybody has searched for anything.
   */
  readonly essential?: true;
}

export const TUTORIALS: readonly TutorialDefinition[] = Object.freeze([
  {
    id: 'first_order',
    topic: 'getting_started',
    defaultTitle: 'Book your first order',
    defaultSummary: 'Take an order at the counter, from the customer’s name to the advance.',
    defaultSteps: [
      'Tap Orders in the menu. The booking form opens straight away — there is no list to get past.',
      'Type the customer’s mobile number. If they have been here before their name fills in; if not, add them without leaving the form.',
      'Add a garment, pick its type, and enter the rate you agreed.',
      'Set the delivery date. If you promised a trial fitting, set that too — it must fall before delivery.',
      'If they paid something now, enter it as the advance. The balance is worked out for you.',
      'Save. The order number is shown large enough to read out across a counter.',
    ],
    route: '/orders/new',
    videoUrl: null,
    essential: true,
  },
  {
    id: 'measurements',
    topic: 'taking_orders',
    defaultTitle: 'Save a customer’s measurements once',
    defaultSummary: 'Measure a customer once and re-use it on every order they place.',
    defaultSteps: [
      'Open the customer from Customers, or from the order you are booking.',
      'Choose Measurements, then the garment type you are measuring for.',
      'Enter the figures. Halves and quarters can be typed as ½ or 0.5 — both are understood.',
      'Save the profile. The next order for this customer offers it instead of asking again.',
      'When a figure changes, edit the profile — orders already booked keep the measurements they were booked with.',
    ],
    route: '/customers',
    videoUrl: null,
    essential: true,
  },
  {
    id: 'the_board',
    topic: 'the_workshop',
    defaultTitle: 'Move work across the board',
    defaultSummary: 'See every garment in the shop and move it along as the work is done.',
    defaultSteps: [
      'Tap Board. Each card is one garment, not one order — a lehenga and a blouse on the same bill move separately.',
      'Drag a card to the next stage, or open it and use the stage control.',
      'A card only offers the stages that garment type actually walks. Your shop chooses those in Settings.',
      'Going backwards is deliberate and asks for a reason, because a garment that goes back is a conversation with somebody.',
    ],
    route: '/board',
    videoUrl: null,
    essential: true,
  },
  {
    id: 'assign_work',
    topic: 'the_workshop',
    defaultTitle: 'Give work to a karigar',
    defaultSummary: 'Assign a garment to the person who will stitch it, at the rate it pays.',
    defaultSteps: [
      'Open Assignments, or open the garment from the board.',
      'Pick the person and the stage. The rate comes from their rate card — you do not retype it.',
      'The karigar sees only their own work, and never sees what the customer was charged.',
      'When they mark it done, it counts towards what they are owed on payout day.',
    ],
    route: '/assignments',
    videoUrl: null,
  },
  {
    id: 'alterations',
    topic: 'the_workshop',
    defaultTitle: 'Take in a garment that came back',
    defaultSummary: 'Record an alteration without disturbing the order it belongs to.',
    defaultSteps: [
      'Tap Alterations, then Book an alteration.',
      'Find the garment. The customer and the order come from it, so nothing can be filed against the wrong name.',
      'Write what the customer said is wrong, and when you promised it back.',
      'Leave the charge at zero if you are doing it for nothing — most re-fits are.',
      'The original order is untouched: its delivery date, its bill and the work the karigar was paid for all stay as they were.',
    ],
    route: '/alterations',
    videoUrl: null,
  },
  {
    id: 'take_payment',
    topic: 'getting_paid',
    defaultTitle: 'Take a payment and give a bill',
    defaultSummary: 'Record money against an order and issue a GST invoice for it.',
    defaultSteps: [
      'Open the order and use Record payment. Cash, UPI, card and bank transfer are all here.',
      'The balance updates on the order, on the customer and on the pending list at once.',
      'When the garment is delivered, issue the invoice. The tax is worked out from the place of supply — you do not choose CGST or IGST yourself.',
      'An invoice that was wrong is cancelled with a credit note, never edited. That is what keeps your GST returns straight.',
    ],
    route: '/payments',
    videoUrl: null,
    essential: true,
  },
  {
    id: 'who_owes',
    topic: 'getting_paid',
    defaultTitle: 'See who still owes you',
    defaultSummary: 'Find every unpaid balance in the shop, oldest first.',
    defaultSteps: [
      'Open Payments and switch to what is outstanding.',
      'Sort by how long it has been owed to find the ones worth a phone call.',
      'Open a row to see the order it is against before you ring anybody.',
    ],
    route: '/payments',
    videoUrl: null,
  },
  {
    id: 'add_staff',
    topic: 'the_people',
    defaultTitle: 'Add somebody who works here',
    defaultSummary: 'Invite a karigar or a manager, and decide what they can see.',
    defaultSteps: [
      'Open Staff and send an invitation to their mobile number.',
      'Choose their role. A tailor sees the work assigned to them and never sees money.',
      'They set their own PIN when they join — you never hold their password.',
      'Set their rate card so their work prices itself as they finish it.',
    ],
    route: '/staff',
    videoUrl: null,
  },
  {
    id: 'attendance',
    topic: 'the_people',
    defaultTitle: 'Mark who is in today',
    defaultSummary: 'Keep the day’s roster, and let the shop mark itself in at the door.',
    defaultSteps: [
      'Open Attendance to mark people present, late or on leave.',
      'Overtime, short permission and leave requests each have their own ledger, so what is owed is never a memory.',
      'A leave request from a karigar waits for your approval — nothing is deducted until you decide.',
      'The door kiosk lets people mark themselves in with their PIN, with no login at all.',
    ],
    route: '/attendance',
    videoUrl: null,
  },
  {
    id: 'shop_settings',
    topic: 'the_shop',
    defaultTitle: 'Set up your shop',
    defaultSummary: 'Your address, your GSTIN, your bank details and how your bills look.',
    defaultSteps: [
      'Open Settings. Everything about how the shop presents itself is here.',
      'Fill in your GSTIN and address — they are printed on every invoice.',
      'Add your bank details if you want customers to be able to transfer to you from the bill.',
      'Choose how your order numbers and invoice numbers are built, before you issue the first one.',
    ],
    route: '/settings',
    videoUrl: null,
  },
  {
    id: 'stage_flows',
    topic: 'the_shop',
    defaultTitle: 'Choose the stages each garment walks',
    defaultSummary: 'A blouse and a lehenga do not go through the same steps. Say so once.',
    defaultSteps: [
      'Open Settings, then the workflow for each garment type.',
      'Turn off the stages that garment never goes through.',
      'The board and the app both read the same list, so what a card offers and what the server allows can never disagree.',
      'Changing this does not move anything already in progress.',
    ],
    route: '/settings/stage-flows',
    videoUrl: null,
  },
  {
    id: 'stock',
    topic: 'the_shop',
    defaultTitle: 'Keep track of fabric and trims',
    defaultSummary: 'Know what is on the shelf, and what an order has already taken.',
    defaultSteps: [
      'Open Stock and add what you hold, in the unit you buy it in.',
      'Issue material to an order from the order itself, so the shelf and the job agree.',
      'Every quantity is worked out from movements in and out — nothing is typed over.',
      'Set a low-stock level and the shop tells you before you run out mid-order.',
    ],
    route: '/stock',
    videoUrl: null,
  },
]);

export function tutorialById(id: string): TutorialDefinition | undefined {
  return TUTORIALS.find((tutorial) => tutorial.id === id);
}

/** The handful somebody needs in their first week. */
export function essentialTutorials(): readonly TutorialDefinition[] {
  return TUTORIALS.filter((tutorial) => tutorial.essential === true);
}

export function tutorialsByTopic(topic: TutorialTopic): readonly TutorialDefinition[] {
  return TUTORIALS.filter((tutorial) => tutorial.topic === topic);
}

/**
 * Free-text search over the catalogue.
 *
 * Matches the title, the summary AND the steps, because somebody searching help types the
 * word they are stuck on — "advance", "credit note", "PIN" — and that word is far more often
 * inside a step than in a title. Folded to lower case and nothing cleverer: a stemmer that
 * gets one Tamil-English query wrong is worse than a substring match that is boringly right.
 */
export function searchTutorials(query: string): readonly TutorialDefinition[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return TUTORIALS;
  return TUTORIALS.filter((tutorial) =>
    [tutorial.defaultTitle, tutorial.defaultSummary, ...tutorial.defaultSteps]
      .join(' ')
      .toLowerCase()
      .includes(needle),
  );
}

/** The catalogue as it crosses the wire. */
export const tutorialSchema = z.object({
  id: z.string(),
  topic: tutorialTopicSchema,
  title: z.string(),
  summary: z.string(),
  steps: z.array(z.string()),
  route: z.string(),
  videoUrl: z.string().nullable(),
  essential: z.boolean(),
});
export type Tutorial = z.infer<typeof tutorialSchema>;

export const tutorialListSchema = z.object({
  topics: z.array(
    z.object({
      topic: tutorialTopicSchema,
      label: z.string(),
      items: z.array(tutorialSchema),
    }),
  ),
});
export type TutorialList = z.infer<typeof tutorialListSchema>;

/** The catalogue, grouped, in the shape the screen renders. */
export function tutorialList(): TutorialList {
  return {
    topics: TUTORIAL_TOPICS.map((topic) => ({
      topic,
      label: TUTORIAL_TOPIC_LABELS[topic],
      items: tutorialsByTopic(topic).map((tutorial) => ({
        id: tutorial.id,
        topic: tutorial.topic,
        title: tutorial.defaultTitle,
        summary: tutorial.defaultSummary,
        steps: [...tutorial.defaultSteps],
        route: tutorial.route,
        videoUrl: tutorial.videoUrl,
        essential: tutorial.essential === true,
      })),
    })),
  };
}
